import { List, ShowButton, TextField, useTable } from "@refinedev/antd";
import { CrudFilter, useNavigation } from "@refinedev/core";
import { Button, Form, Space, Table, TablePaginationConfig } from "antd";
import { useMatch, useSearchParams } from "react-router-dom";
import { leagueIndex } from "../Lib";
import { dateFormat, DayMonthYear, errorNotification } from "../../../helpers";
import type { MatchesGrid, Match } from "../Lib/types";
import s from "./Matches-List.module.scss";
import { TourFilter } from "./Filters/Tour-Filter";
import { AppTitle } from "../../../components";
import { useEffect, useMemo, useState } from "react";
import { GroupFilter } from "./Filters/Group-Filter";

type TablePaginationProps = TablePaginationConfig & { total: number };

export const MatchesList = () => {
  const route = useMatch("/:parent/:league/:season/:seasonId");
  const resource = route?.params.league;
  const seasonId = route?.params.seasonId;
  const { push } = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clear, setClear] = useState<boolean>(false);
  const [groupId, setGroupId] = useState<number | undefined>();

  // Получаем значения фильтров из URL
  const tourIdFromUrl = searchParams.get("tourId");
  const groupIdFromUrl = searchParams.get("groupId");

  const { tableProps, searchFormProps, setFilters } = useTable<MatchesGrid>({
    dataProviderName: "matchesDataProvider",
    resource: "matches",
    meta: {
      leagueId: leagueIndex[`${resource}`],
      seasonId,
      clear,
    },
    syncWithLocation: true,
    queryOptions: {
      enabled: true,
    },
    ...errorNotification({
      errorData: {
        errorKeys: [
          {
            errorCode: "channelPoints.invalid",
            message:
              "Невозможно добавить баллы без создания маркетингового канала",
          },
        ],
      },
    }),
    onSearch: (params: any) => {
      const filters: CrudFilter[] = [];
      const { tourId, groupId } = params;

      filters.push({
        field: "tourId",
        operator: "null",
        value: tourId || undefined,
      });

      filters.push({
        field: "groupId",
        operator: "null",
        value: groupId || undefined,
      });

      // Обновляем URL параметры
      const newSearchParams = new URLSearchParams(searchParams);
      if (tourId) {
        newSearchParams.set("tourId", tourId.toString());
      } else {
        newSearchParams.delete("tourId");
      }
      if (groupId) {
        newSearchParams.set("groupId", groupId.toString());
      } else {
        newSearchParams.delete("groupId");
      }
      setSearchParams(newSearchParams);

      return filters || [];
    },
  });

  // Восстанавливаем значения фильтров из URL при монтировании
  useEffect(() => {
    if (resource && seasonId) {
      const formValues: { tourId?: number; groupId?: number } = {};

      if (tourIdFromUrl) {
        formValues.tourId = Number(tourIdFromUrl);
      }
      if (groupIdFromUrl) {
        const groupIdNum = Number(groupIdFromUrl);
        formValues.groupId = groupIdNum;
        setGroupId(groupIdNum);
      }

      // Устанавливаем значения формы
      if (Object.keys(formValues).length > 0) {
        setTimeout(() => {
          searchFormProps.form?.setFieldsValue(formValues);
        }, 100);
      }
    }
  }, [resource, seasonId, tourIdFromUrl, groupIdFromUrl, searchFormProps.form]);

  const tableLimit = useMemo(() => {
    const total = (tableProps.pagination as TablePaginationProps).total;
    return total >= 10;
  }, [tableProps.pagination]);

  const handleNavigateToMatches = (matchId: number) => {
    push(`show/${matchId}`);
  };

  const watchGroup = Form.useWatch("groupId", searchFormProps.form);
  const watchTour = Form.useWatch("tourId", searchFormProps.form);

  const handleRefetch = () => {
    setClear(true);
    searchFormProps.form?.resetFields();
    setFilters([]);

    // Очищаем URL параметры
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("tourId");
    newSearchParams.delete("groupId");
    setSearchParams(newSearchParams);

    setTimeout(() => {
      setClear(false);
    }, 100);
  };

  useEffect(() => {
    searchFormProps.form?.resetFields(["tourId"]);
  }, [groupId, searchFormProps.form]);

  // Обновляем URL при изменении groupId
  useEffect(() => {
    if (groupId !== undefined) {
      const newSearchParams = new URLSearchParams(searchParams);
      if (groupId) {
        newSearchParams.set("groupId", groupId.toString());
      } else {
        newSearchParams.delete("groupId");
      }
      setSearchParams(newSearchParams);
    }
  }, [groupId, searchParams, setSearchParams]);

  return (
    <List
      title={<AppTitle canGoBack>Матчи</AppTitle>}
      resource={"matches"}
      breadcrumb={false}
      canCreate={false}
      wrapperProps={{ style: { position: "sticky", top: 24 } }}
    >
      <Space size={8} direction="vertical" style={{ width: "100%" }}>
        <Form
          {...searchFormProps}
          layout="horizontal"
          className={s.modalContainerSearch}
        >
          {leagueIndex[`${resource}`] !== "100" && (
            <>
              <GroupFilter
                seasonId={Number(seasonId)}
                leagueId={Number(leagueIndex[`${resource}`])}
                onChangeGroup={setGroupId}
              />
            </>
          )}
          <TourFilter
            seasonId={Number(seasonId)}
            leagueId={Number(leagueIndex[`${resource}`])}
            groupId={groupId}
          />
          <Button disabled={!watchTour && !watchGroup} onClick={handleRefetch}>
            Очистить фильтры
          </Button>

          <Button
            onClick={() => {
              searchFormProps.form?.submit();
            }}
            htmlType="submit"
            type="primary"
          >
            Применить
          </Button>
        </Form>
        <Table
          {...tableProps}
          scroll={{ x: 1200 }}
          rowKey="id"
          locale={{
            emptyText: <div style={{ margin: 56 }}>Матчи отсутствуют</div>,
          }}
          showSorterTooltip={false}
          pagination={
            tableLimit
              ? {
                  ...tableProps.pagination,
                  pageSizeOptions: [10, 20, 50],
                }
              : false
          }
        >
          <Table.Column<Match>
            dataIndex={"tour"}
            key="tour"
            title="Тур"
            render={(value) => <TextField value={value} />}
          />
          {leagueIndex[`${resource}`] !== "100" && (
            <Table.Column<Match>
              dataIndex={"group"}
              key="group"
              title="Группа"
              render={(value) => <TextField value={value} />}
            />
          )}
          <Table.Column<Match>
            dataIndex={"date"}
            key="date"
            title="Дата"
            render={(value) => (
              <TextField value={dateFormat(value, DayMonthYear)} />
            )}
          />
          <Table.Column<Match>
            key="name"
            title="Команды"
            render={(value) => {
              return (
                <Space direction={"vertical"}>
                  <Space>
                    <TextField value={"Домашняя команда:"} />
                    <TextField value={value?.home.name ?? "–"} />
                  </Space>
                  <Space>
                    <TextField value={"Гостевая команда:"} />
                    <TextField value={value?.guest.name ?? "–"} />
                  </Space>
                </Space>
              );
            }}
          />
          <Table.Column<Match>
            key="stadium"
            title="Стадион"
            render={(value) => {
              return (
                <Space direction={"vertical"}>
                  <Space>
                    <TextField value={"Название:"} />
                    <TextField value={value?.stadium.title ?? "–"} />
                  </Space>
                  <Space>
                    <TextField value={"Город:"} />
                    <TextField value={value?.stadium.city ?? "–"} />
                  </Space>
                </Space>
              );
            }}
          />
          <Table.Column<Match>
            width={"15%"}
            title={"Действия"}
            align={"center"}
            dataIndex="actions"
            render={(_, record) => (
              <ShowButton
                size="small"
                onClick={() => handleNavigateToMatches(record.matchId)}
              >
                Посмотреть
              </ShowButton>
            )}
          />
        </Table>
      </Space>
    </List>
  );
};
